import { useEffect, useState } from "react";

import { Store, StoreComponentStatus } from "../../../../types";
import {
    DeploymentBranchesResult,
    NetlifySiteResult,
    getDeploymentBranches,
    getMirrorHealth,
    getNetlifySite,
    getSearchIndexHealth,
    pingShopifyStore,
} from "../../../../lib/storeComponents";

export type ComponentStatuses = {
    searchIndex: StoreComponentStatus;
    mirror: StoreComponentStatus;
    shopify: StoreComponentStatus;
    basicTheme: DeploymentBranchesResult;
    hydrogenV2: DeploymentBranchesResult;
    netlify: NetlifySiteResult;
};

const BRANCHES_CHECKING: DeploymentBranchesResult = { status: "checking", branchCount: 0 };
const SITE_CHECKING: NetlifySiteResult = { status: "checking", name: null, adminUrl: null };

function useProbe<T>(probe: () => Promise<T>, initial: T, dependencies: unknown[]): T {
    const [result, setResult] = useState<T>(initial);

    useEffect(() => {
        let hasUnmounted = false;
        setResult(initial);

        probe().then((value) => {
            if (!hasUnmounted) setResult(value);
        });

        return () => {
            hasUnmounted = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);

    return result;
}

/** Checks every component of the store again whenever refreshKey changes. */
export function useComponentStatuses(store: Store, refreshKey: number): ComponentStatuses {
    const { id, storeUrl, hydrogenStorefrontToken } = store;

    return {
        searchIndex: useProbe(() => getSearchIndexHealth(id), "checking", [id, refreshKey]),
        mirror: useProbe(() => getMirrorHealth(id), "checking", [id, refreshKey]),
        shopify: useProbe(() => pingShopifyStore(store), "checking", [storeUrl, hydrogenStorefrontToken, refreshKey]),
        basicTheme: useProbe(() => getDeploymentBranches(id, "basic-theme"), BRANCHES_CHECKING, [id, refreshKey]),
        hydrogenV2: useProbe(() => getDeploymentBranches(id, "hydrogen-v2"), BRANCHES_CHECKING, [id, refreshKey]),
        netlify: useProbe(() => getNetlifySite(id), SITE_CHECKING, [id, refreshKey]),
    };
}
